import type { RouteObject } from 'react-router-dom';
import { ProtectedRoute, PublicRoute } from '../components/auth/ProtectedRoute';
import { lazy, type ComponentType } from 'react';

// ⭐ 미리 로드할 컴포넌트들
const preloadComponents: Record<string, () => Promise<{ default: ComponentType }>> = {
  Home: () => import('../pages/home/page'),
  Booking: () => import('../pages/booking/page'),
  Notifications: () => import('../pages/notifications/page'),
  Profile: () => import('../pages/profile/page'),
};

// ⭐ 앱 시작 시 주요 페이지 미리 로드
export const preloadMainPages = () => {
  Object.values(preloadComponents).forEach(loader => {
    loader();
  });
};

// ⭐ 주요 페이지는 preloadComponents 재사용
const Home = lazy(preloadComponents.Home);
const Booking = lazy(preloadComponents.Booking);
const Notifications = lazy(preloadComponents.Notifications);
const Profile = lazy(preloadComponents.Profile);

// 나머지 페이지들은 일반 lazy
const PatientEditPage = lazy(() => import('../pages/profile/patient/edit/page'));
const CaregiverEditPage = lazy(() => import('../pages/profile/caregiver/edit/page'));
const PhoneEditPage = lazy(() => import('../pages/profile/phone/edit/page'));
const Login = lazy(() => import('../pages/login/page'));
const Search = lazy(() => import('../pages/search/page'));
const BookingDetail = lazy(() => import('../pages/booking/detail/page'));
const BookingApplicants = lazy(() => import('../pages/booking/applicants/page'));
const ApplicantDetail = lazy(() => import('../pages/booking/applicants/detail/page'));
const Contact = lazy(() => import('../pages/contact/page'));
const CareRequestStep1 = lazy(() => import('../pages/care-request/step1/page'));
const CareRequestStep2 = lazy(() => import('../pages/care-request/step2/page'));
const CareRequestStep3 = lazy(() => import('../pages/care-request/step3/page'));
const CareRequestStep4 = lazy(() => import('../pages/care-request/step4/page'));
const Insurance = lazy(() => import('../pages/insurance/page'));
const InsuranceDetail = lazy(() => import('../pages/insurance/detail/page'));
const PlusMall = lazy(() => import('../pages/plus-mall/page'));
const NotFound = lazy(() => import('../pages/NotFound'));

import CaregiverJobSearch from '../pages/caregiver/job-search/page';
import CaregiverJobDetail from '../pages/caregiver/job-detail/page';
import CaregiverJobApply from '../pages/caregiver/job-detail/apply/page';

// ⭐ 간병일지 관련 (통일)
const CareLogPage = lazy(() => import('../pages/caregiver/care-log/page'));
const DailyCareLogPage = lazy(() => import('../pages/caregiver/care-log/daily/page'));
const WriteCareLogPage = lazy(() => import('../pages/caregiver/care-log/write/page'));
const CareLogDetailPage = lazy(() => import('../pages/caregiver/care-log/daily/detail/page'));

const CaregiverInquiryPage = lazy(() => import('../pages/caregiver/inquiry/page'));
import SettlementPage from '../pages/settlement/page';
const SettlementDetailPage = lazy(() => import('../pages/settlement/detail/page'));
const CaregiverSchedulePage = lazy(() => import('../pages/caregiver/schedule/page'));
const SignupPage = lazy(() => import('../pages/signup/page'));
const PhoneVerificationPage = lazy(() => import('../pages/signup/phone-verification/page'));
const PatientAccountPage = lazy(() => import('../pages/signup/patient/account/page'));
const CaregiverAccountPage = lazy(() => import('../pages/signup/caregiver/account/page'));
const CaregiverProfilePage = lazy(() => import('../pages/signup/caregiver/profile/page'));
const AccountPage = lazy(() => import('../pages/account/page'));
const SentQuotes = lazy(() => import('../pages/profile/sent-quotes/page'));
const WithdrawalPage = lazy(() => import('../pages/profile/withdrawal/page'));
const NoticePage = lazy(() => import('../pages/notice/page'));
const NoticeDetailPage = lazy(() => import('../pages/notice/detail/page'));
const FAQPage = lazy(() => import('../pages/faq/page'));
const FAQDetailPage = lazy(() => import('../pages/faq/detail/page'));
const PrivacyPolicyPage = lazy(() => import('../pages/policy/privacy/page'));
const ServiceTermsPage = lazy(() => import('../pages/policy/service/page'));
const AiEthicsPage = lazy(() => import('../pages/policy/ai-ethics/page'));
const CareHistoryPage = lazy(() => import('../pages/care-history/page'));

const FindIdPage = lazy(() => import('../pages/find-id/page'));
const FindIdResultPage = lazy(() => import('../pages/find-id/result/page'));
const FindPasswordPage = lazy(() => import('../pages/find-password/page'));
const FindPasswordResultPage = lazy(() => import('../pages/find-password/result/page'));

const ReviewPage = lazy(() => import('../pages/review/page'));
const ReviewWritePage = lazy(() => import('../pages/review/write/page'));

const routes: RouteObject[] = [
  // 로그인/회원가입
  {
    path: '/login',
    element: <PublicRoute><Login /></PublicRoute>,
  },
  {
    path: '/find-id',
    element: <FindIdPage />,
  },
  {
    path: '/find-id/result',
    element: <FindIdResultPage />,
  },
  {
    path: '/find-password',
    element: <FindPasswordPage />,
  },
  {
    path: '/find-password/result',
    element: <FindPasswordResultPage />,
  },
  {
    path: '/',
    element: <Home />,
  },
  {
    path: '/search',
    element: <Search />,
  },
  {
    path: '/contact',
    element: <Contact />,
  },

  // 로그인 필요한 페이지들
  {
    path: '/notifications',
    element: <ProtectedRoute><Notifications /></ProtectedRoute>,
  },
  {
    path: '/booking',
    element: <ProtectedRoute><Booking /></ProtectedRoute>,
  },
  {
    path: '/booking/detail/:careNo',
    element: <BookingDetail />,
  },
  {
    path: '/booking/:careNo/applicants',
    element: <ProtectedRoute><BookingApplicants /></ProtectedRoute>,
  },
  {
    path: '/booking/:careNo/applicants/:estimateNo',
    element: <ProtectedRoute><ApplicantDetail /></ProtectedRoute>,
  },
  {
    path: '/profile',
    element: <ProtectedRoute><Profile /></ProtectedRoute>,
  },
  {
    path: '/profile/patient/edit',
    element: <ProtectedRoute><PatientEditPage /></ProtectedRoute>,
  },
  {
    path: '/profile/caregiver/edit',
    element: <ProtectedRoute><CaregiverEditPage /></ProtectedRoute>,
  },
  {
    path: '/profile/phone/edit',
    element: <ProtectedRoute><PhoneEditPage /></ProtectedRoute>,
  },
  {
    path: '/care-request/step1',
    element: <ProtectedRoute><CareRequestStep1 /></ProtectedRoute>,
  },
  {
    path: '/care-request/step2',
    element: <ProtectedRoute><CareRequestStep2 /></ProtectedRoute>,
  },
  {
    path: '/care-request/step3',
    element: <ProtectedRoute><CareRequestStep3 /></ProtectedRoute>,
  },
  {
    path: '/care-request/step4',
    element: <ProtectedRoute><CareRequestStep4 /></ProtectedRoute>,
  },
  {
    path: '/insurance',
    element: <ProtectedRoute><Insurance /></ProtectedRoute>,
  },
  {
    path: '/insurance/:careNo',
    element: <ProtectedRoute><InsuranceDetail /></ProtectedRoute>,
  },

  // ⭐ 플러스몰 (제휴사 배너)
  {
    path: '/plus-mall',
    element: <ProtectedRoute><PlusMall /></ProtectedRoute>,
  },

  // ⭐ 간병일지 (통일된 경로 - 보호자/간병인 공용)
  {
    path: '/caregiver/care-log',
    element: <ProtectedRoute><CareLogPage /></ProtectedRoute>,
  },
  {
    path: '/caregiver/care-log/:careNo',
    element: <ProtectedRoute><DailyCareLogPage /></ProtectedRoute>,
  },
  {
    path: '/caregiver/care-log/:careNo/write',
    element: <ProtectedRoute><WriteCareLogPage /></ProtectedRoute>,
  },
  {
    path: '/caregiver/care-log/:careNo/daily/:careNoteNo',
    element: <ProtectedRoute><CareLogDetailPage /></ProtectedRoute>,
  },

  // 간병인 페이지들
  {
    path: '/caregiver/job-search',
    element: <ProtectedRoute><CaregiverJobSearch /></ProtectedRoute>,
  },
  {
    path: '/caregiver/job-detail/:jobId',
    element: <CaregiverJobDetail />,
  },
  {
    path: '/caregiver/job-detail/:jobId/apply',
    element: <CaregiverJobApply />,
  },
  {
    path: '/caregiver/inquiry',
    element: <ProtectedRoute><CaregiverInquiryPage /></ProtectedRoute>,
  },
  {
    path: '/caregiver/schedule',
    element: <ProtectedRoute><CaregiverSchedulePage /></ProtectedRoute>,
  },
  {
    path: '/settlement',
    element: <ProtectedRoute><SettlementPage /></ProtectedRoute>,
  },
  {
    path: '/settlement/:paidNo',
    element: <ProtectedRoute><SettlementDetailPage /></ProtectedRoute>,
  },
  {
    path: '/signup',
    element: <SignupPage />,
  },
  {
    path: '/signup/phone-verification',
    element: <PhoneVerificationPage />,
  },
  {
    path: '/signup/patient/account',
    element: <PatientAccountPage />,
  },
  {
    path: '/signup/caregiver/account',
    element: <CaregiverAccountPage />,
  },
  {
    path: '/signup/caregiver/profile',
    element: <CaregiverProfilePage />,
  },
  {
    path: '/account',
    element: <AccountPage />,
  },
  {
    path: '/profile/sent-quotes',
    element: <SentQuotes />,
  },
  {
    path: '/care-history',
    element: <CareHistoryPage />,
  },
  {
    path: '/profile/withdrawal',
    element: <WithdrawalPage />,
  },
  {
    path: '/review',
    element: <ReviewPage />,
  },
  {
    path: '/review/write',
    element: <ReviewWritePage />,
  },
  {
    path: '/notice',
    element: <NoticePage />,
  },
  {
    path: '/notice/:notifyNo',
    element: <NoticeDetailPage />,
  },
  {
    path: '/faq',
    element: <FAQPage />,
  },
  {
    path: '/faq/:faqNo',
    element: <FAQDetailPage />,
  },
  {
    path: '/policy/privacy',
    element: <PrivacyPolicyPage />,
  },
  {
    path: '/policy/service',
    element: <ServiceTermsPage />,
  },
  {
    path: '/policy/ai-ethics',
    element: <AiEthicsPage />,
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;